import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider } from "@/hooks/use-auth";
import { TwoFactorGuard } from "@/components/TwoFactorGuard";
import { lazy, Suspense } from "react";

// Load critical pages immediately
import NotFound from "@/pages/not-found";
import Home from "@/pages/home";

// Lazy load other pages
const Login = lazy(() => import("@/pages/login"));
const Register = lazy(() => import("@/pages/register"));
const ForgotPassword = lazy(() => import("@/pages/forgot-password"));
const ResetPassword = lazy(() => import("@/pages/reset-password"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const Track = lazy(() => import("@/pages/track"));
const Course = lazy(() => import("@/pages/course-overview"));
const Topic = lazy(() => import("@/pages/topic"));
const Courses = lazy(() => import("@/pages/courses"));
const Certifications = lazy(() => import("@/pages/certifications"));
const PlaygroundFixed = lazy(() => import("@/pages/playground-fixed"));
const StarterKits = lazy(() => import("@/pages/starter-kits"));
const Subscribe = lazy(() => import("@/pages/subscribe"));
const PaymentPlans = lazy(() => import("@/pages/payment-plans"));
const Checkout = lazy(() => import("@/pages/checkout"));
const PaymentSuccess = lazy(() => import("@/pages/payment-success"));
const PaymentStatus = lazy(() => import("@/pages/payment-status"));
const Documentation = lazy(() => import("@/pages/documentation"));
const CommunityForum = lazy(() => import("@/pages/community-forum"));
const BlogTutorials = lazy(() => import("@/pages/blog-tutorials"));
const ResearchPapers = lazy(() => import("@/pages/research-papers"));
const IndustryReports = lazy(() => import("@/pages/industry-reports"));
const CareerGuide = lazy(() => import("@/pages/career-guide"));
const HelpCenter = lazy(() => import("@/pages/help-center"));
const ContactUs = lazy(() => import("@/pages/contact-us"));
const TechnicalSupport = lazy(() => import("@/pages/technical-support"));
const AccessibilityPage = lazy(() => import("@/pages/accessibility"));
const PrivacyPolicy = lazy(() => import("@/pages/privacy-policy"));
const TermsOfService = lazy(() => import("@/pages/terms-of-service"));
const TestNavigation = lazy(() => import("@/pages/test-navigation"));
const NavigationTest = lazy(() => import("@/pages/navigation-test"));
const Profile = lazy(() => import("@/pages/profile"));
const SettingsPage = lazy(() => import("@/pages/settings"));
const VerifyCertificate = lazy(() => import("@/pages/verify-certificate"));
const Roadmap = lazy(() => import("@/pages/roadmap"));
const AICareerRoadmap = lazy(() => import("@/pages/ai-career-roadmap"));
const AdminDashboard = lazy(() => import("@/pages/admin"));
const CareerModule = lazy(() => import("@/pages/career"));
const AITestDashboard = lazy(() => import("@/pages/ai-test-dashboard"));
const Contact = lazy(() => import("@/pages/contact"));
const Privacy = lazy(() => import("@/pages/privacy"));
const Terms = lazy(() => import("@/pages/terms"));
const ProgrammaticSeoPage = lazy(() => import("@/pages/programmatic-seo"));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/home" component={() => { window.location.replace('/'); return null; }} />
        <Route path="/index" component={() => { window.location.replace('/'); return null; }} />
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/track/:trackId" component={Track} />
        <Route path="/courses" component={Courses} />
        <Route path="/course/:courseId" component={Course} />
        <Route path="/courses/:courseId" component={Course} />
        <Route path="/learn/:slug" component={ProgrammaticSeoPage} />
        <Route path="/course/:courseId/chapter/:chapterId/topic/:topicId" component={Topic} />
        <Route path="/topic/:topicId" component={Topic} />
        <Route path="/certifications" component={Certifications} />
        <Route path="/verify-certificate/:verificationId" component={VerifyCertificate} />
        <Route path="/roadmap" component={Roadmap} />
        <Route path="/admin" component={AdminDashboard} />
        <Route path="/playground" component={PlaygroundFixed} />
        <Route path="/starter-kits" component={StarterKits} />
        <Route path="/profile" component={Profile} />
        <Route path="/settings" component={SettingsPage} />
        <Route path="/payment-plans" component={PaymentPlans} />
        <Route path="/subscribe" component={() => <PaymentPlans />} />
        <Route path="/checkout" component={Checkout} />
        <Route path="/payment-success" component={PaymentSuccess} />
        <Route path="/payment-status" component={PaymentStatus} />
        <Route path="/login" component={Login} />
        <Route path="/register" component={Register} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/test" component={TestNavigation} />
        <Route path="/navtest" component={NavigationTest} />
        
        {/* Resources */}
        <Route path="/documentation" component={Documentation} />
        <Route path="/community" component={CommunityForum} />
        <Route path="/blog" component={BlogTutorials} />
        <Route path="/research" component={ResearchPapers} />
        <Route path="/reports" component={IndustryReports} />
        <Route path="/career-guide" component={CareerGuide} />
        <Route path="/career" component={CareerModule} />
        <Route path="/career/*" component={CareerModule} />
        <Route path="/ai-careers" component={AICareerRoadmap} />
        <Route path="/ai-test" component={AITestDashboard} />
        
        {/* Support */}
        <Route path="/help" component={HelpCenter} />
        <Route path="/contact" component={Contact} />
        <Route path="/support" component={TechnicalSupport} />
        <Route path="/accessibility" component={AccessibilityPage} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/terms" component={Terms} />
        
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TwoFactorGuard>
            <TooltipProvider>
              <Toaster />
              <Router />
            </TooltipProvider>
          </TwoFactorGuard>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
